import type { ChatDocumentDTO, ChatSourceReferenceDTO } from '@shared/schemas/chat';
import { chunkAnchorSchema, type ChunkAnchor } from '@shared/schemas/rag-anchors';
import { apiBaseUrl } from '@/lib/auth-client';
import type { ActiveSourceReference } from './chat-experience';
import { DOCUMENT_CITATION_KEY_PATTERN } from './chat-citation-markers';
import { safeWebCitationUrl } from './chat-web-citations';
import type { ChatUIMessage } from './types';

type ChatPart = ChatUIMessage['parts'][number];
export type WebUrlSourcePart = Extract<ChatPart, { type: 'source-url' }>;
export type DocumentSourcePart = Extract<ChatPart, { type: 'source-document' }>;

/** One cited company document, opened on its first passage. */
export type DocumentSourceEntry = {
  document: ChatDocumentDTO;
  key: string;
  reference: ActiveSourceReference | null;
  references: ActiveSourceReference[];
};

/** One passage cited inline with a `[D#]` key. */
export type DocumentCitation = {
  citationKey: string;
  document: ChatDocumentDTO;
  documentKey: string;
  reference: ActiveSourceReference | null;
};

export type SourceLocation = { kind: 'page' | 'slide'; number: number };

function readRecord(part: DocumentSourcePart) {
  return part as unknown as Record<string, unknown>;
}

function readNonEmptyString(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function getStreamedRagSourceParts(message: ChatUIMessage): DocumentSourcePart[] {
  return message.parts.flatMap((part) => (
    part.type === 'data-rag-citations'
      ? part.data.citations.map((citation) => ({ ...citation, type: 'source-document' as const }) as DocumentSourcePart)
      : []
  ));
}

export function getWebUrlSourceParts(message: ChatUIMessage): WebUrlSourcePart[] {
  return message.parts.filter((part): part is WebUrlSourcePart => (
    part.type === 'source-url' && safeWebCitationUrl(part.url) !== null
  ));
}

/**
 * Persisted (hydrated) document citations win; a live message only has the
 * streamed `data-rag-citations` part, shown once the stream has settled.
 * Web parts never hide document parts of the same mixed answer.
 */
export function getDocumentSourceParts(message: ChatUIMessage, includeStreamedRagSources: boolean) {
  const persisted = message.parts.filter((part): part is DocumentSourcePart => part.type === 'source-document');
  if (persisted.length > 0) {
    return persisted;
  }
  return includeStreamedRagSources ? getStreamedRagSourceParts(message) : [];
}

/** True when the backend served these passages from a degraded retrieval (vector or rerank fallback). */
export function hasDegradedRetrieval(parts: readonly DocumentSourcePart[]) {
  return parts.some((part) => readRecord(part).retrievalDegraded === true && !isRedactedSourcePart(part));
}

export function isRedactedSourcePart(part: DocumentSourcePart) {
  return readRecord(part).accessStatus === 'redacted';
}

function getSourceAnchor(part: DocumentSourcePart): ChunkAnchor | null {
  const result = chunkAnchorSchema.safeParse(readRecord(part).anchor ?? null);
  return result.success ? result.data : null;
}

function getSourceCitationPage(part: DocumentSourcePart) {
  const value = readRecord(part).citationPage;
  return typeof value === 'number' ? value : null;
}

function resolveSourceReferencePage(part: DocumentSourcePart, anchor: ChunkAnchor | null) {
  const citationPage = getSourceCitationPage(part);
  if (citationPage && citationPage > 0) {
    return citationPage;
  }
  if (!anchor) {
    return null;
  }
  if (anchor.kind === 'pdf') {
    return anchor.page;
  }
  if (anchor.kind === 'pptx') {
    return anchor.slide;
  }
  return 1;
}

function toReferenceAnchor(reference: ChatSourceReferenceDTO, partAnchor: ChunkAnchor | null): ChunkAnchor | null {
  const referenceAnchor = chunkAnchorSchema.safeParse(reference.anchor ?? null);
  if (referenceAnchor.success && referenceAnchor.data) {
    return referenceAnchor.data;
  }
  if (partAnchor?.kind === 'pdf') {
    return { bbox: partAnchor.page === reference.page ? partAnchor.bbox : null, kind: 'pdf', page: reference.page };
  }
  if (partAnchor?.kind === 'pptx') {
    return { kind: 'pptx', shapeId: partAnchor.shapeId, slide: reference.page };
  }
  return partAnchor;
}

function toActiveSourceReference(reference: ChatSourceReferenceDTO, anchor: ChunkAnchor | null): ActiveSourceReference {
  return {
    anchor,
    blockId: reference.blockId,
    chunkRerankScore: reference.chunkRerankScore,
    endLine: reference.endLine,
    excerpt: reference.excerpt,
    ...(reference.highlights?.length ? { highlights: reference.highlights } : {}),
    isVerifiable: reference.isVerifiable,
    page: reference.page,
    referenceId: reference.referenceId,
    sectionPath: reference.sectionPath,
    source: reference.source,
    startLine: reference.startLine,
  };
}

export function getSourceReferences(part: DocumentSourcePart): ActiveSourceReference[] {
  const record = readRecord(part);
  const anchor = getSourceAnchor(part);
  if (Array.isArray(record.references)) {
    const parsed = record.references.flatMap((reference: unknown) => {
      if (!reference || typeof reference !== 'object') {
        return [];
      }
      const value = reference as ChatSourceReferenceDTO;
      if (!value.referenceId || typeof value.page !== 'number' || value.page < 1) {
        return [];
      }
      return [toActiveSourceReference(value, toReferenceAnchor(value, anchor))];
    });
    if (parsed.length > 0) {
      return parsed.slice(0, 12);
    }
  }

  const excerpt = readNonEmptyString(record, 'excerpt');
  const citationPage = resolveSourceReferencePage(part, anchor);
  if (!excerpt || !citationPage || citationPage < 1) {
    return [];
  }
  return [{
    anchor,
    excerpt,
    page: citationPage,
    referenceId: readNonEmptyString(record, 'citationBlockId') ?? `${part.sourceId}:fallback`,
  }];
}

function getDocumentKey(part: DocumentSourcePart) {
  return readNonEmptyString(readRecord(part), 'citationDocumentId') ?? part.sourceId;
}

export function toCitationDocument(part: DocumentSourcePart): ChatDocumentDTO {
  const record = readRecord(part);
  const title = part.title ?? 'Source documentaire';
  const filename = part.filename ?? null;
  const excerpt = readNonEmptyString(record, 'excerpt');
  const citationPage = resolveSourceReferencePage(part, getSourceAnchor(part));
  const citationDocumentId = readNonEmptyString(record, 'citationDocumentId');
  const sourceUrl = citationDocumentId
    ? new URL(`/api/documents/${citationDocumentId}/content`, apiBaseUrl).toString()
    : readNonEmptyString(record, 'url');
  const content = [
    `## ${title}`,
    typeof citationPage === 'number' ? `- Page ${citationPage}` : null,
    filename && filename !== title ? `- Document: ${filename}` : null,
    excerpt ? `\n${excerpt}` : '\nAperçu non disponible.',
  ].filter(Boolean).join('\n');

  return {
    content,
    documentId: citationDocumentId ?? `rag-citation-${part.sourceId}`,
    filename,
    kind: 'attachment' as const,
    mediaType: part.mediaType ?? 'application/pdf',
    sourceUrl,
    status: 'ready' as const,
    summary: excerpt?.slice(0, 180) ?? title,
    title,
  };
}

export function getDocumentDisplayName(document: ChatDocumentDTO) {
  return document.filename ?? document.title ?? 'Source documentaire';
}

/** Page (PDF, table) or slide (PPTX); flowing formats have no stable page. */
export function getSourceLocation(reference: ActiveSourceReference | null): SourceLocation | null {
  const anchor = reference?.anchor;
  if (anchor?.kind === 'pdf') return { kind: 'page', number: anchor.page };
  if (anchor?.kind === 'pptx') return { kind: 'slide', number: anchor.slide };
  if (anchor || !reference || reference.page < 1) return null;
  return { kind: 'page', number: reference.page };
}

export function formatSourceLocation(location: SourceLocation, style: 'long' | 'short') {
  if (location.kind === 'slide') {
    return style === 'long' ? `diapositive ${location.number}` : `diapo. ${location.number}`;
  }
  return style === 'long' ? `page ${location.number}` : `p. ${location.number}`;
}

/** One entry per document (first passage first), for the sources list. */
export function buildDocumentSourceEntries(messageId: string, parts: readonly DocumentSourcePart[]): DocumentSourceEntry[] {
  const entries = new Map<string, { document: ChatDocumentDTO; references: ActiveSourceReference[] }>();

  for (const part of parts) {
    if (isRedactedSourcePart(part)) continue;
    const references = getSourceReferences(part);
    if (references.length === 0) continue;
    const documentKey = getDocumentKey(part);
    const existing = entries.get(documentKey);
    if (existing) {
      existing.references.push(...references);
    } else {
      entries.set(documentKey, { document: toCitationDocument(part), references: [...references] });
    }
  }

  return [...entries.entries()].map(([documentKey, entry]) => {
    const seen = new Set<string>();
    const references = entry.references.filter((reference) => {
      if (seen.has(reference.referenceId)) return false;
      seen.add(reference.referenceId);
      return true;
    });
    return { document: entry.document, key: `${messageId}-${documentKey}`, reference: references[0] ?? null, references };
  });
}

/** Keyed passages that an inline badge can open; redacted citations never qualify. */
export function collectDocumentCitations(parts: readonly DocumentSourcePart[]): DocumentCitation[] {
  const citations = new Map<string, DocumentCitation>();
  for (const part of parts) {
    const citationKey = readNonEmptyString(readRecord(part), 'citationKey');
    if (!citationKey || !DOCUMENT_CITATION_KEY_PATTERN.test(citationKey) || citations.has(citationKey)
      || isRedactedSourcePart(part) || !readNonEmptyString(readRecord(part), 'citationDocumentId')) {
      continue;
    }
    citations.set(citationKey, {
      citationKey,
      document: toCitationDocument(part),
      documentKey: getDocumentKey(part),
      reference: getSourceReferences(part)[0] ?? null,
    });
  }
  return [...citations.values()];
}
